import React, { createContext, useCallback, useContext, useState } from "react";

// In-memory app state that lives for as long as the app stays mounted and is
// discarded on a full page reload — it is never persisted. Keyed by an
// arbitrary string so unrelated features can keep their own independent slices
// (e.g. per-grid column widths). Use this for state that should survive
// navigation within the SPA but intentionally reset on reload.
type EphemeralState = { [key: string]: unknown };

// Split into value + setter contexts (à la WithFlashes) so the setter identity
// stays stable while only the value changes.
const EphemeralStateContext = createContext<EphemeralState | null>(null);
const SetEphemeralStateContext = createContext<
  ((key: string, value: unknown) => void) | null
>(null);

const WithEphemeralAppState = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<EphemeralState>({});
  const setForKey = useCallback((key: string, value: unknown) => {
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);
  return (
    <SetEphemeralStateContext.Provider value={setForKey}>
      <EphemeralStateContext.Provider value={state}>
        {children}
      </EphemeralStateContext.Provider>
    </SetEphemeralStateContext.Provider>
  );
};

// Read and write a single ephemeral key, with a useState-like signature. When
// rendered under WithEphemeralAppState the value is shared app-wide (and so
// survives a component unmount/remount during navigation); without a provider
// it gracefully falls back to component-local state, so consumers like Grid
// keep working in isolation (tests, Storybook). Either way the value is in
// memory only and resets on reload.
export function useEphemeralAppState<T>(
  key: string,
): [T | undefined, (value: T) => void] {
  const sharedState = useContext(EphemeralStateContext);
  const setShared = useContext(SetEphemeralStateContext);
  const usingShared = sharedState !== null && setShared !== null;
  // Always declared (hooks can't be conditional); only used as the fallback.
  const [localState, setLocalState] = useState<EphemeralState>({});

  const value = (usingShared ? sharedState[key] : localState[key]) as
    | T
    | undefined;
  const setValue = useCallback(
    (next: T) => {
      if (usingShared) {
        setShared(key, next);
      } else {
        setLocalState((prev) => ({ ...prev, [key]: next }));
      }
    },
    [usingShared, setShared, key],
  );
  return [value, setValue];
}

export default WithEphemeralAppState;
